import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Row,
  Col,
  Button,
  Alert,
  Form,
  InputGroup,
} from 'react-bootstrap';
import SelectComponent from '../Select';
import useConvenios from '../../../hooks/useConvenios';
import useProcedimentos from '../../../hooks/useProcedimentos';
import useGetNomeMedico from '../../../hooks/useGetNomeMedico';
import { goToNextInput } from '../../../util/utils';

const FaturamentoModalEdit = ({
  show,
  onHide,
  fatura,
  setFatura,
  selectMedicos,
  medicos,
  handleChangeFaturamento,
  handleValorFaturamento,
  handleUpdateFaturamento,
  handleExcluirFaturamento,
}) => {
  const { convenios, opcoesConvenio } = useConvenios();
  const { opcoesProcedimento } = useProcedimentos();
  const [showAlertFaturamento, setShowAlertFaturamento] = useState(false);
  const handleGetNomeMedico = useGetNomeMedico(medicos, setFatura);

  const isLiberado = fatura.liberado === 'SIM';
  const canEdit = !isLiberado;
  const isProcediThree = Number(fatura.codProcedi) === 3;

  useEffect(() => {
    if (!show) {
      setShowAlertFaturamento(false);
    }
  }, [show]);

  const handleConfirmUpdate = async e => {
    const ok = await handleUpdateFaturamento(e);
    if (ok) {
      setShowAlertFaturamento(false);
    }
  };

  const handleChangeEditField = e => {
    if (!canEdit) return;

    // Captura o valor síncrono para evitar perda de referência do evento synthetic
    const { name, value } = e.target;

    if (name === 'valor' && isProcediThree) {
      setFatura(prev => ({
        ...prev,
        valor: value,
        valorecg: value,
      }));
      return;
    }

    handleChangeFaturamento(e);
  };

  const handleSetFaturaEdit = useCallback(
    next => {
      if (!canEdit) return;
      setFatura(next);
    },
    [canEdit, setFatura]
  );

  const handleChangeDataFatura = e => {
    if (!canEdit) return;
    const nextDate = e.target.value;

    setFatura(prev => ({
      ...prev,
      dataFatura: nextDate,
      dataFormatada: nextDate,
    }));
  };

  const handleConvKeyUp = e => {
    if (!canEdit || e.key !== 'Enter') return;

    const selectedCod = Number(e.target.value);
    const convEncontrado = convenios.find(c => Number(c.codConv) === selectedCod);

    if (convEncontrado) {
      setFatura(prev => ({ ...prev, convenio: convEncontrado.sigla }));
      goToNextInput(e, 'codProcedi');
    } else {
      goToNextInput(e, 'codConv');
    }
  };

  const handleFocusGravarEdit = e => {
    if (!canEdit) return;
    if (e.key === 'Enter') {
      document.getElementById('btnAtualizar')?.focus();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header className="font-weight-bold text-danger" closeButton>
        ATUALIZA FATURAMENTO {fatura.codFatura} - LAUDO: {fatura.codLaudo}
      </Modal.Header>

      <Modal.Body>
        {isLiberado && (
          <Alert variant="warning" className="mb-2">
            Faturamento já liberado. Não é possível alterar ou excluir.
          </Alert>
        )}

        <Row className="align-items-center">
          {/* Data */}
          <Form.Group as={Col} sm={2} className="m-0" size="sm">
            <Form.Label className="m-0">Data:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={10} className="m-0">
            <Form.Control
              type="date"
              size="sm"
              name="dataFatura"
              id="dataFatura"
              readOnly={!canEdit}
              disabled={!canEdit}
              aria-label="Data do faturamento"
              value={fatura.dataFatura || ''}
              onChange={handleChangeDataFatura}
            />
          </Form.Group>

          {/* Paciente */}
          <Form.Group as={Col} sm={2} className="m-0" size="sm">
            <Form.Label className="m-0">Paciente:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="m-0" size="sm">
            <Form.Control
              readOnly
              size="sm"
              name="codPac"
              value={fatura.codPac ?? ''}
            />
          </Form.Group>
          <Form.Group as={Col} sm={8} className="m-0" size="sm">
            <InputGroup size="sm" className="m-0">
              <Form.Control
                readOnly
                size="sm"
                name="paciente"
                value={fatura.paciente ?? ''}
              />
            </InputGroup>
          </Form.Group>

          {/* Conveniado */}
          <Form.Group as={Col} sm={2} className="m-0" size="sm">
            <Form.Label className="m-0">Conveniado:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Control
              size="sm"
              type="number"
              id="codConveniado"
              disabled={!canEdit}
              onKeyUp={e =>
                canEdit && handleGetNomeMedico(e, 'codSolicitou', 'conveniado')
              }
              onChange={handleChangeEditField}
              name="codConveniado"
              value={fatura.codConveniado ?? ''}
            />
          </Form.Group>
          <Form.Group as={Col} sm={8} className="m-0">
            <SelectComponent
              name="conveniado"
              codValue={fatura.codConveniado}
              setFatura={handleSetFaturaEdit}
              isDisabled={!canEdit}
              options={selectMedicos}
              codField="codConveniado"
              textField="conveniado"
              placeholder="Selecione um Conveniado"
            />
          </Form.Group>

          {/* Solicitou */}
          <Form.Group as={Col} sm={2} className="m-0" size="sm">
            <Form.Label className="m-0">Solicitou:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Control
              size="sm"
              type="number"
              id="codSolicitou"
              disabled={!canEdit}
              onKeyUp={e =>
                canEdit && handleGetNomeMedico(e, 'codRealizou', 'solicitou')
              }
              onChange={handleChangeEditField}
              name="codSolicitou"
              value={fatura.codSolicitou ?? ''}
            />
          </Form.Group>
          <Form.Group as={Col} sm={8} className="m-0">
            <SelectComponent
              name="solicitou"
              codValue={fatura.codSolicitou}
              setFatura={handleSetFaturaEdit}
              isDisabled={!canEdit}
              options={selectMedicos}
              codField="codSolicitou"
              textField="solicitou"
              placeholder="Selecione quem Solicitou"
            />
          </Form.Group>

          {/* Realizou */}
          <Form.Group as={Col} sm={2} className="m-0" size="sm">
            <Form.Label className="m-0">Realizou:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Control
              size="sm"
              type="number"
              id="codRealizou"
              disabled={!canEdit}
              onKeyUp={e =>
                canEdit && handleGetNomeMedico(e, 'codFaturou', 'realizou')
              }
              onChange={handleChangeEditField}
              name="codRealizou"
              value={fatura.codRealizou ?? ''}
            />
          </Form.Group>
          <Form.Group as={Col} sm={8} className="m-0">
            <SelectComponent
              name="realizou"
              codValue={fatura.codRealizou}
              setFatura={handleSetFaturaEdit}
              isDisabled={!canEdit}
              options={selectMedicos}
              codField="codRealizou"
              textField="realizou"
              placeholder="Selecione quem Realizou"
            />
          </Form.Group>

          {/* Faturou */}
          <Form.Group as={Col} sm={2} className="m-0" size="sm">
            <Form.Label className="m-0">Faturou:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Control
              size="sm"
              type="number"
              id="codFaturou"
              disabled={!canEdit}
              onKeyUp={e =>
                canEdit && handleGetNomeMedico(e, 'codConv', 'faturou')
              }
              onChange={handleChangeEditField}
              name="codFaturou"
              value={fatura.codFaturou ?? ''}
            />
          </Form.Group>
          <Form.Group as={Col} sm={8} className="m-0">
            <SelectComponent
              name="faturou"
              codValue={fatura.codFaturou}
              setFatura={handleSetFaturaEdit}
              isDisabled={!canEdit}
              options={selectMedicos}
              codField="codFaturou"
              textField="faturou"
              placeholder="Selecione quem Faturou"
            />
          </Form.Group>

          {/* Convênio */}
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Label className="m-0">Convênio:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Control
              size="sm"
              type="number"
              id="codConv"
              disabled={!canEdit}
              onKeyUp={handleConvKeyUp}
              onChange={handleChangeEditField}
              name="codConv"
              value={fatura.codConv ?? ''}
            />
          </Form.Group>
          <Form.Group as={Col} sm={8} className="m-0">
            <SelectComponent
              name="convenio"
              codValue={fatura.codConv}
              setFatura={handleSetFaturaEdit}
              isDisabled={!canEdit}
              options={opcoesConvenio}
              codField="codConv"
              textField="convenio"
              placeholder="Selecione um Convênio"
            />
          </Form.Group>

          {/* Procedimento */}
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Label className="m-0">Proced:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="m-0">
            <Form.Control
              size="sm"
              id="codProcedi"
              type="number"
              style={{ backgroundColor: '#fff3cd' }}
              disabled={!canEdit}
              onKeyUp={e => {
                if (!canEdit) return;
                if (e.key === 'Enter') goToNextInput(e, 'valor');
              }}
              onBlur={e => canEdit && handleValorFaturamento(e)}
              onChange={handleChangeEditField}
              name="codProcedi"
              value={fatura.codProcedi ?? ''}
            />
          </Form.Group>
          <Form.Group as={Col} sm={8} className="m-0">
            <SelectComponent
              name="procedimento"
              codValue={fatura.codProcedi}
              setFatura={handleSetFaturaEdit}
              isDisabled={!canEdit}
              options={opcoesProcedimento}
              codField="codProcedi"
              textField="procedimento"
              placeholder="Selecione um Procedimento"
            />
          </Form.Group>

          {/* Usuário / Guia / Valor */}
          <Form.Group as={Col} sm={2} className="mt-1" size="sm">
            <Form.Label className="m-0">Usuário:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="mt-1">
            <Form.Control
              readOnly
              size="sm"
              name="usuario"
              value={fatura.usuario ?? ''}
            />
          </Form.Group>

          <Form.Group as={Col} sm={1} className="mt-1" size="sm">
            <Form.Label className="m-0">Guia:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="mt-1">
            <Form.Control
              size="sm"
              type="number"
              id={isProcediThree ? 'guiaecg' : 'guia'}
              disabled={!canEdit}
              onKeyUp={e => canEdit && goToNextInput(e, 'valor')}
              onChange={handleChangeEditField}
              name={isProcediThree ? 'guiaecg' : 'guia'}
              value={(isProcediThree ? fatura.guiaecg : fatura.guia) ?? ''}
            />
          </Form.Group>

          <Form.Group as={Col} sm={1} className="mt-1" size="sm">
            <Form.Label className="m-0">Valor:</Form.Label>
          </Form.Group>
          <Form.Group as={Col} sm={2} className="mt-1">
            <Form.Control
              size="sm"
              type="number"
              id="valor"
              disabled={!canEdit}
              onKeyUp={handleFocusGravarEdit}
              onChange={handleChangeEditField}
              name="valor"
              value={fatura.valor ?? ''}
            />
          </Form.Group>
        </Row>

        <div className="text-center mt-3">
          <Button
            id="btnAtualizar"
            disabled={!canEdit}
            onClick={() => setShowAlertFaturamento(true)}
            aria-label="Gravar alterações do faturamento"
          >
            Gravar
          </Button>{' '}
          <Button
            variant="danger"
            disabled={!canEdit}
            onClick={handleExcluirFaturamento}
            aria-label="Excluir faturamento"
          >
            Excluir
          </Button>{' '}
          <Button variant="secondary" onClick={onHide}>
            Fechar
          </Button>
        </div>

        <Alert
          show={showAlertFaturamento && canEdit}
          variant="warning"
          className="mt-3 text-right"
        >
          <Alert.Heading>
            Confirma a Atualização do Faturamento?
          </Alert.Heading>
          <Button
            id="btnUpdateFat"
            disabled={!canEdit}
            onClick={handleConfirmUpdate}
            variant="warning"
          >
            Atualizar Faturamento
          </Button>
        </Alert>
      </Modal.Body>
    </Modal>
  );
};

export default memo(FaturamentoModalEdit);